import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { t } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Allekirjoitusruutu (T621, Toni 19.10.2026 klo 11.50).

   Asiakkaan edustaja allekirjoittaa sormella tai kynällä. Osoitintapahtumat (pointer events) kattavat kosketuksen, kynän ja
   hiiren; `touch-action: none` estää sivun vierityksen piirrettäessä. Kangas skaalataan näytön pikselitiheyteen, jotta viiva on
   terävä tabletilla. Kuva luetaan PNG:nä (`kuva()`), ja tyhjä ruutu on tyhjä eikä valkoinen kuva.
   ═══════════════════════════════════════════════════════════ */

export interface AllekirjoitusruutuKahva { kuva: () => string | null; tyhjenna: () => void }

const Allekirjoitusruutu = forwardRef<AllekirjoitusruutuKahva, { muuttui?: (piirretty: boolean) => void; korkeus?: number }>(
  function Allekirjoitusruutu({ muuttui, korkeus = 160 }, kahva) {
    const kangas = useRef<HTMLCanvasElement>(null);
    const piirtaa = useRef(false);
    const [piirretty, setPiirretty] = useState(false);

    const valmistele = useCallback(() => {
      const c = kangas.current; if (!c) return;
      const r = c.getBoundingClientRect(); const s = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.round(r.width * s)); c.height = Math.max(1, Math.round(r.height * s));
      const g = c.getContext("2d"); if (!g) return;
      g.setTransform(s, 0, 0, s, 0, 0); g.lineWidth = 2.2; g.lineCap = "round"; g.lineJoin = "round"; g.strokeStyle = "#111";
    }, []);
    useEffect(() => { valmistele(); }, [valmistele]);

    const tyhjenna = useCallback(() => {
      const c = kangas.current; if (!c) return;
      c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      setPiirretty(false); muuttui?.(false);
    }, [muuttui]);

    useImperativeHandle(kahva, () => ({
      kuva: () => (piirretty && kangas.current ? kangas.current.toDataURL("image/png") : null),
      tyhjenna,
    }), [piirretty, tyhjenna]);

    const piste = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const r = e.currentTarget.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    function alas(e: React.PointerEvent<HTMLCanvasElement>) {
      e.preventDefault();
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* kaappaus ei ole välttämätön piirtämiselle */ }
      // Piilotetussa välilehdessä (nipun työtila) kangas oli 0 px leveä asennushetkellä: mitoitetaan ensimmäisellä kosketuksella.
      const c = e.currentTarget, s = window.devicePixelRatio || 1;
      if (!piirretty && c.width !== Math.max(1, Math.round(c.getBoundingClientRect().width * s))) valmistele();
      const g = e.currentTarget.getContext("2d"); if (!g) return;
      const p = piste(e); piirtaa.current = true; g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(p.x + 0.1, p.y + 0.1); g.stroke();
      if (!piirretty) { setPiirretty(true); muuttui?.(true); }
    }
    function liike(e: React.PointerEvent<HTMLCanvasElement>) {
      if (!piirtaa.current) return; e.preventDefault();
      const g = e.currentTarget.getContext("2d"); if (!g) return;
      const p = piste(e); g.lineTo(p.x, p.y); g.stroke();
    }
    const ylos = () => { piirtaa.current = false; };

    return (
      <div className="allekirjoitusruutu" data-allekirjoitusruutu={piirretty ? "piirretty" : "tyhja"}>
        <canvas ref={kangas} aria-label={t("ltar.allekirjoita_ohje")}
          style={{ width: "100%", maxWidth: 480, height: korkeus, display: "block", border: "1px solid var(--viiva)",
                   background: "#fff", touchAction: "none", cursor: "crosshair" }}
          onPointerDown={alas} onPointerMove={liike} onPointerUp={ylos} onPointerCancel={ylos} onPointerLeave={ylos} />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
          <span className="dim" style={{ flex: 1 }}>{t("ltar.allekirjoita_ohje")}</span>
          <button type="button" className="nappi nappi--kevyt" data-allekirjoitus-tyhjenna="" onClick={tyhjenna} disabled={!piirretty}>
            {t("ltar.tyhjenna")}</button>
        </div>
      </div>
    );
  });

export default Allekirjoitusruutu;
